import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

import BasicDetailTab from 'src/sections/dashboard-sections/BasicDetailTab';
import PropertyImagesTab from 'src/sections/dashboard-sections/PropertyImagesTab';
import PropertyDocumentsTab from 'src/sections/dashboard-sections/PropertyDocumentsTab';
import VideosTab from 'src/sections/dashboard-sections/VideosTab';
import FactsFeaturesTab from 'src/sections/dashboard-sections/FactsFeaturesTab';
import FloorPlansTab from 'src/sections/dashboard-sections/FloorPlansTab';
import StagedImagesTab from 'src/sections/dashboard-sections/StagedImagesTab';
import View360Tab from 'src/sections/dashboard-sections/View360Tab';
import ViewOnMap from 'src/sections/dashboard-sections/ViewOnMap';
import SeoTab from 'src/sections/dashboard-sections/SeoTab';

import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Typography,
  Button,
  Divider,
  Skeleton,
  Stack
} from '@mui/material';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { useLoading } from 'src/contexts/LoadingContext';
import {
  getStatus,
  getTypes,
  getFeatures,
  uploadPropertyMedia,
  uploadPropertyDocuments,
  getPropertyById,
  updateProperty,
  UploadFloorPlans,
  getProperties,
  uploadStaggedImages,
  getCitiesTable,
  addProperty,
  getAreaUnits,
  getCityMatch,
  get_state_for_properties,
  get_cities_for_properties
} from 'src/ApiServices';
import { property } from 'lodash';

export default function AddProperties() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = Boolean(editId);
  const { loadingStates, startLoading, stopLoading, isAnythingLoading } = useLoading();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });
  const [page, setPage] = useState(1);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [featuresOptions, setFeaturesOptions] = useState([]);
  const [areaUnitsOptions, setAreaUnitsOptions] = useState([]);
  const [basicTabData, setBasicTabData] = useState({});
  const [basicTabDataForSEO, setBasicTabDataForSEO] = useState({
    title: '',
    state: '',
    city: '',
    description: '',
    state_name: '',
    city_name: ''
  });

  const [uploadingImages, setUploadingImages] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState([]);
  const [propertyData, setpropertyData] = useState();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [looadingStates, setLooadingStates] = useState(false);
  const [uploadingVideoThumbnails, setUploadingVideoThumbnails] = useState({
    property: [],
    drone: []
  });

  // Google Maps
  const GoogleJs_apiKey = process.env.REACT_APP_GOOGLE_LOCATION_API_KEY || '';
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Main form state
  const [form, setForm] = useState({
    activeTab: 0,
    title: '',
    address: '',
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    area: '',
    lat: null,
    lon: null,
    description: '',
    status: '',
    type: '',
    areaUnit: '',
    buildUpArea: '',
    lotSize: '',
    showArea: false,
    price: '',
    floorPrice: '',
    floorSize: '',
    exclusiveAuthority: '',
    buildYear: '',
    showBuildYear: false,
    bedrooms: '',
    bathrooms: '',
    garage: '',
    garageSize: '',
    is_show_on_website: true,
    is_featured: false,
    is_area_unit: false,
    show_nearby: false,
    video_chat: false,
    view360Url: '',
    images: [],
    documents: [],
    propertyVideo: null,
    droneVideo: null,
    features: {},
    floorPlans: [],
    expandedFloorPlan: null,
    stagedImages: [],
    view360Images: [],
    propertyVideoThumbnail: null,
    droneVideoThumbnail: null
  });

  // Fancybox initialization
  useEffect(() => {
    Fancybox.bind('[data-fancybox]', {
      Thumbs: false,
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close']
        }
      }
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  // Fetch states on mount
  const fetchStates = async () => {
    setLooadingStates(true);
    try {
      const res = await get_state_for_properties();
      setStates(res.data || []);
    } catch (err) {
      console.error('Failed to fetch states:', err);
      toast.error('Failed to load states');
    } finally {
      setLooadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return [];
    }

    setLoadingCities(true);
    try {
      const res = await get_cities_for_properties(stateId);
      const citiesData = res?.data || [];
      setCities(citiesData);
      return citiesData;
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      toast.error('Failed to load cities');
      setCities([]);
      return [];
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch cities when state changes
  useEffect(() => {
    if (form.state) {
      fetchCities(form.state);
    } else {
      setCities([]);
      setField('city', '');
    }
  }, [form.state]);

  // States load on component mount
  useEffect(() => {
    fetchStates();
  }, []);

  const handleBasicTabData = (basicData) => {
    // console.log('Data received from BasicDetailTab:', basicData);

    setForm((prev) => ({
      ...prev,
      // Jo fields child ne bheje hain unko update karo
      title: basicData.title ?? prev.title,
      city: basicData.city ?? prev.city,
      state: basicData.state ?? prev.state,
      description: basicData.description ?? prev.description
    }));
  };

  const handleVideoThumbnailUpload = async (e, videoType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    const loadingKey = `uploading_${videoType}_thumbnail`;
    startLoading(loadingKey);

    try {
      setUploadingVideoThumbnails((prev) => ({
        ...prev,
        [videoType]: [
          {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isUploading: true
          }
        ]
      }));

      // Set file_type based on video type
      const fileType = videoType === 'property' ? 'video_thumbnail' : 'drone_thumbnail';

      const response = await uploadPropertyMedia(file, fileType);

      const thumbnailObj = {
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        id: response.data?.id || response.id,
        serverUrl: response.data?.file || response.url,
        isUploading: false,
        uploadError: false
      };

      if (videoType === 'property') {
        setField('propertyVideoThumbnail', thumbnailObj);
      } else {
        setField('droneVideoThumbnail', thumbnailObj);
      }

      setUploadingVideoThumbnails((prev) => ({
        ...prev,
        [videoType]: []
      }));

      toast.success('Thumbnail uploaded successfully!');
    } catch (error) {
      console.error(`${videoType} video thumbnail upload failed:`, error);
      toast.error('Thumbnail upload failed. Please try again.');

      setUploadingVideoThumbnails((prev) => ({
        ...prev,
        [videoType]: []
      }));
    } finally {
      stopLoading(loadingKey);
      e.target.value = null;
    }
  };

  const removeVideoThumbnail = (videoType) => {
    if (videoType === 'property') {
      setField('propertyVideoThumbnail', null);
    } else {
      setField('droneVideoThumbnail', null);
    }
    toast.success('Thumbnail removed');
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setField('city', '');
    setField('state', stateId);
    if (stateId) {
      fetchCities(stateId);
    }
  };
  useEffect(() => {
    if (!GoogleJs_apiKey) {
      setError('Google Maps API key is missing. Please contact support.');
      return;
    }

    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setIsScriptLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GoogleJs_apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsScriptLoaded(true);
      script.onerror = () => {
        setError('Failed to load Google Maps script.');
        console.error('Google Maps script failed to load');
      };
      document.head.appendChild(script);
    };

    try {
      loadGoogleMapsScript();
    } catch (err) {
      console.error('Error loading Google Maps:', err);
      setError('Error loading Google Maps');
    }
  }, [GoogleJs_apiKey]);

  const triggerAddressSearch = (searchText) => {
    if (!isScriptLoaded || !window.google || !window.google.maps || !inputRef.current) {
      return;
    }

    try {
      if (!inputRef.current._autocomplete) {
        inputRef.current._autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ['address'],
            componentRestrictions: { country: 'us' }
          }
        );

        inputRef.current._autocomplete.addListener('place_changed', () => {
          const place = inputRef.current._autocomplete.getPlace();
          handlePlaceSelect(place);
        });
      }

      const service = new window.google.maps.places.AutocompleteService();
      if (searchText.length > 3) {
        service.getPlacePredictions(
          {
            input: searchText,
            componentRestrictions: { country: 'us' },
            types: ['address']
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              // console.log('Predictions found:', predictions.length);
            }
          }
        );
      }
    } catch (err) {
      console.error('Error triggering address search:', err);
    }
  };

  const handlePlaceSelect = (place) => {
    if (!place || !place.formatted_address) {
      return;
    }

    const lat = place.geometry?.location?.lat();
    const lng = place.geometry?.location?.lng();

    // Extract address components
    const getComponent = (type, useShortName = false) => {
      const component = place.address_components?.find((comp) => comp.types.includes(type));
      return component ? (useShortName ? component.short_name : component.long_name) : '';
    };

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const locality = getComponent('locality');
    const administrativeArea = getComponent('administrative_area_level_1', true);
    const administrativeAreaLong = getComponent('administrative_area_level_1');
    const country = getComponent('country');
    const postalCode = getComponent('postal_code');
    const sublocality = getComponent('sublocality');
    const neighborhood = getComponent('neighborhood');

    const streetAddress = `${streetNumber} ${route}`.trim();

    // CITY SELECTION
    let selectedCity = locality || sublocality || neighborhood || '';

    // STATE SELECTION
    let selectedStateId = '';
    if (administrativeAreaLong && states && Array.isArray(states)) {
      const matchedState = states.find(
        (state) =>
          state.name.toLowerCase() === administrativeAreaLong.toLowerCase() ||
          state.abbreviation?.toLowerCase() === administrativeAreaLong.toLowerCase()
      );
      if (matchedState) {
        selectedStateId = matchedState.id;
      }
    }

    // Set form values
    setForm((prev) => ({
      ...prev,
      address: place.formatted_address,
      streetAddress,
      city: selectedCity,
      state: selectedStateId,
      country: country || 'United States',
      postalCode: postalCode || '',
      area: sublocality || locality || '',
      lat: lat || null,
      lon: lng || null
    }));

    // Fetch cities when state is selected
    if (selectedStateId) {
      fetchCities(selectedStateId).then(() => {
        if (selectedCity && cities.length > 0) {
          const matchedCity = cities.find(
            (c) =>
              c.name.toLowerCase().includes(selectedCity.toLowerCase()) ||
              selectedCity.toLowerCase().includes(c.name.toLowerCase())
          );

          if (matchedCity) {
            setTimeout(() => {
              setForm((prev) => ({
                ...prev,
                city: matchedCity.id
              }));
            }, 100);
          }
        }
      });
    }
  };

  // Google Maps autocomplete
  useEffect(() => {
    if (isScriptLoaded && inputRef.current && !error) {
      try {
        if (inputRef.current._autocomplete) {
          window.google.maps.event.clearInstanceListeners(inputRef.current);
        }
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: {
            country: 'us'
          }
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();

          if (place.formatted_address) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            //Extract Address components
            const getComponent = (type, useShortName = false) => {
              const component = place.address_components?.find((comp) => comp.types.includes(type));
              return component ? (useShortName ? component.short_name : component.long_name) : '';
            };

            const streetNumber = getComponent('street_number');
            const route = getComponent('route');
            const locality = getComponent('locality');
            const administrativeArea = getComponent('administrative_area_level_1', true);
            const administrativeAreaLong = getComponent('administrative_area_level_1');
            const country = getComponent('country');
            const postalCode = getComponent('postal_code');
            const sublocality = getComponent('sublocality');
            const neighborhood = getComponent('neighborhood');

            const streetAddress = `${streetNumber} ${route}`.trim();

            // CITY SELECTION LOGIC
            let selectedCity = '';
            let selectedCityId = '';

            // Priority order for city detection
            const city = locality || sublocality || neighborhood || '';

            // STATE SELECTION LOGIC
            let selectedStateId = '';
            if (administrativeAreaLong && states && Array.isArray(states)) {
              const matchedState = states.find(
                (state) =>
                  state.name.toLowerCase() === administrativeAreaLong.toLowerCase() ||
                  state.abbreviation?.toLowerCase() === administrativeAreaLong.toLowerCase()
              );
              if (matchedState) {
                selectedStateId = matchedState.id;
              }
            }

            // Set form values first
            setForm((prev) => ({
              ...prev,
              address: place.formatted_address,
              streetAddress,
              city: city,
              state: selectedStateId,
              country: country || 'United States',
              postalCode: postalCode || '',
              area: sublocality || locality || '',
              lat: lat,
              lon: lng
            }));

            // console.log('Address selected from Google:', {
            //   address: place.formatted_address,
            //   state: administrativeAreaLong,
            //   stateId: selectedStateId,
            //   city: city
            // });

            // Fetch cities when state is selected
            if (selectedStateId) {
              fetchCities(selectedStateId).then(() => {
                if (city && cities.length > 0) {
                  const matchedCity = cities.find(
                    (c) =>
                      c.name.toLowerCase().includes(city.toLowerCase()) ||
                      city.toLowerCase().includes(c.name.toLowerCase())
                  );

                  if (matchedCity) {
                    setTimeout(() => {
                      setForm((prev) => ({
                        ...prev,
                        city: matchedCity.id
                      }));
                    }, 500);
                  }
                }
              });
            }
          }
        });
      } catch (err) {
        console.error('Google Maps autocomplete error:', err);
        setError('Google Maps autocomplete failed');
      }
    }
  }, [isScriptLoaded, states, cities]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        startLoading('loadingStatus');

        const statusRes = await getStatus().catch((err) => {
          return null;
        });

        const typesRes = await getTypes().catch((err) => {
          return null;
        });

        const featuresRes = await getFeatures().catch((err) => {
          return null;
        });

        const areaUnits = await getAreaUnits().catch((err) => {
          return null;
        });

        if (statusRes?.data) setStatusOptions(statusRes.data);
        if (typesRes?.data) setTypeOptions(typesRes.data);
        if (featuresRes?.data) setFeaturesOptions(featuresRes.data);
        if (areaUnits?.data) setAreaUnitsOptions(areaUnits.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        stopLoading('loadingStatus');
      }
    };

    fetchData();
  }, []);

  // Fetch property data for edit mode
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!isEditMode || !editId) return;

      try {
        startLoading('loadingProperty');
        const response = await getPropertyById(editId);

        let propertyData;
        if (response.data) {
          propertyData = Array.isArray(response.data) ? response.data[0] : response.data;
        } else {
          propertyData = response;
        }

        // console.log(`Property data for ideee  ${editId}:`, propertyData);
        // console.log(`Property data for ideee slug ${editId}:`, propertyData?.slug);
        // console.log(
        //   `Property data for ideee meta description  ${editId}:`,
        //   propertyData?.meta_description
        // );
        // console.log(`Property data for ideee meta title  ${editId}:`, propertyData?.meta_title);

        if (propertyData) {
          // Format all data
          const formattedImages = (propertyData.images || []).map((img, index) => ({
            id: img.id,
            preview: img.file || img.url || img.image_url || img.path,
            name: img.name || `image_${index + 1}`,
            isDefault: Boolean(img.is_main || img.is_default),
            serverUrl: img.file || img.url || img.image_url || img.path,
            isUploading: false
          }));

          const formattedPropertyVideo = propertyData.video
            ? typeof propertyData.video === 'object'
              ? {
                  id: propertyData.video.id || null,
                  serverUrl:
                    propertyData.video.file || propertyData.video.url || propertyData.video,
                  preview: propertyData.video.file || propertyData.video.url || propertyData.video,
                  name: 'Property Video'
                }
              : {
                  serverUrl: propertyData.video,
                  preview: propertyData.video,
                  name: 'Property Video'
                }
            : null;

          const formattedDroneVideo = propertyData.drone_video
            ? typeof propertyData.drone_video === 'object'
              ? {
                  id: propertyData.drone_video.id || null,
                  serverUrl:
                    propertyData.drone_video.file ||
                    propertyData.drone_video.url ||
                    propertyData.drone_video,
                  preview:
                    propertyData.drone_video.file ||
                    propertyData.drone_video.url ||
                    propertyData.drone_video,
                  name: 'Drone Video'
                }
              : {
                  serverUrl: propertyData.drone_video,
                  preview: propertyData.drone_video,
                  name: 'Drone Video'
                }
            : null;

          const formattedDocuments = (propertyData.attachments || propertyData.documents || []).map(
            (doc) => ({
              id: doc.id,
              serverUrl: doc.file || doc.url,
              name: doc.name || doc.file?.split('/').pop() || 'Document',
              isUploading: false
            })
          );

          const formattedFeatures = {};
          if (propertyData.features && Array.isArray(propertyData.features)) {
            propertyData.features.forEach((category) => {
              // Check for features directly in category (if any)
              if (category.features && Array.isArray(category.features)) {
                category.features.forEach((feature) => {
                  formattedFeatures[feature.name] = true;
                });
              }

              // Check for features in sub_categories
              if (category.sub_categories && Array.isArray(category.sub_categories)) {
                category.sub_categories.forEach((subCategory) => {
                  if (subCategory.features && Array.isArray(subCategory.features)) {
                    subCategory.features.forEach((feature) => {
                      formattedFeatures[feature.name] = true;
                    });
                  }
                });
              }
            });
          }

          const formattedFloorPlans = (propertyData.floor_plans || []).map((fp) => {
            let imageObj = null;
            const imageUrl = fp.floor_image;

            if (imageUrl) {
              imageObj = {
                id: fp.image_id || fp.id,
                serverUrl: imageUrl,
                preview: imageUrl,
                name: fp.title || 'Floor Plan Image',
                isUploading: false,
                uploadError: false
              };
            }

            return {
              id: fp.id,
              title: fp.title || `Floor Plan ${fp.id}`,
              bedrooms: fp.bedrooms?.toString() || '',
              bathrooms: fp.bathrooms?.toString() || '',
              floorPrice: fp.price?.toString() || '',
              floorSize: fp.size?.toString() || '',
              notes: fp.description || '',
              image: imageObj
            };
          });

          const formattedStagedImages = (propertyData.propertyStageImages || []).map(
            (stagePair, index) => {
              const fromImage = stagePair.from_image || {};
              const toImage =
                stagePair.to_images && stagePair.to_images.length > 0
                  ? stagePair.to_images[0]
                  : null;
              const mainImageUrl =
                fromImage.file || fromImage.url || fromImage.image_url || fromImage.path;

              const stagedImg = {
                id: fromImage.id || stagePair.id,
                preview: mainImageUrl,
                name: fromImage.name || `staged_image_${index + 1}`,
                isDefault: Boolean(fromImage.is_default),
                serverUrl: mainImageUrl,
                isUploading: false,
                uploadError: false
              };

              if (toImage) {
                const pairedImageUrl =
                  toImage.file || toImage.url || toImage.image_url || toImage.path;
                stagedImg.paired = {
                  id: toImage.id,
                  preview: pairedImageUrl,
                  name: toImage.name || 'Paired Image',
                  serverUrl: pairedImageUrl,
                  isUploading: false,
                  uploadError: false
                };
              }

              return stagedImg;
            }
          );

          const formattedView360Url = propertyData.view_3d || propertyData.view_360;

          const formattedPropertyVideoThumbnail = propertyData.video_thumbnail
            ? typeof propertyData.video_thumbnail === 'object'
              ? {
                  id: propertyData.video_thumbnail.id || null,
                  serverUrl:
                    propertyData.video_thumbnail.file ||
                    propertyData.video_thumbnail.url ||
                    propertyData.video_thumbnail,
                  preview:
                    propertyData.video_thumbnail.file ||
                    propertyData.video_thumbnail.url ||
                    propertyData.video_thumbnail,
                  name: propertyData.video_thumbnail.name || 'Property Video Thumbnail',
                  isUploading: false,
                  uploadError: false
                }
              : {
                  serverUrl: propertyData.video_thumbnail,
                  preview: propertyData.video_thumbnail,
                  name: 'Property Video Thumbnail',
                  isUploading: false,
                  uploadError: false
                }
            : null;

          const formattedDroneVideoThumbnail = propertyData.drone_video_thumbnail
            ? typeof propertyData.drone_video_thumbnail === 'object'
              ? {
                  id: propertyData.drone_video_thumbnail.id || null,
                  serverUrl:
                    propertyData.drone_video_thumbnail.file ||
                    propertyData.drone_video_thumbnail.url ||
                    propertyData.drone_video_thumbnail,
                  preview:
                    propertyData.drone_video_thumbnail.file ||
                    propertyData.drone_video_thumbnail.url ||
                    propertyData.drone_video_thumbnail,
                  name: propertyData.drone_video_thumbnail.name || 'Drone Video Thumbnail',
                  isUploading: false,
                  uploadError: false
                }
              : {
                  serverUrl: propertyData.drone_video_thumbnail,
                  preview: propertyData.drone_video_thumbnail,
                  name: 'Drone Video Thumbnail',
                  isUploading: false,
                  uploadError: false
                }
            : null;
          const formattedData = {
            title: propertyData.title || '',
            address: propertyData.address || '',
            streetAddress: propertyData.street_address || propertyData.streetAddress || '',
            // city: propertyData.city || '',
            // state: propertyData.state || '',
            country: propertyData.country || '',
            postalCode: propertyData.postal_code || propertyData.postalCode || '',
            area: propertyData.area || '',
            lat: propertyData.lat
              ? parseFloat(propertyData.lat)
              : propertyData.latitude
              ? parseFloat(propertyData.latitude)
              : null,
            lon: propertyData.lon
              ? parseFloat(propertyData.lon)
              : propertyData.longitude
              ? parseFloat(propertyData.longitude)
              : null,
            state: propertyData.state_id || '',
            city: propertyData.city_id || '',
            postal_code: propertyData.postal_code || '',
            description: propertyData.description || '',
            status: propertyData?.property_status || propertyData?.property_status_id || '',
            type:
              propertyData.property_type?.name ||
              propertyData.property_type ||
              propertyData.type?.name ||
              '',
            areaUnit: propertyData.area_unit?.name || propertyData.area_unit || '',
            slug: propertyData.slug || '',
            meta_title: propertyData.meta_title || '',
            meta_description: propertyData.meta_description || '',

            buildUpArea: propertyData.area_size?.toString() || '',
            lotSize: propertyData.lot_size?.toString() || '',
            showArea: Boolean(propertyData.is_show_area),
            price: propertyData.price?.toString() || '',
            buildYear: propertyData.year_built ? `${propertyData.year_built}` : '',
            showBuildYear: Boolean(propertyData.is_show_build_year),
            bedrooms: propertyData.bedrooms?.toString() || '',
            bathrooms: propertyData.bathrooms?.toString() || '',
            // garage: propertyData.garage?.toString() || '',
            is_garage:
              propertyData.is_garage === 'active' ||
              propertyData.is_garage === true ||
              propertyData.is_garage === 1 ||
              propertyData.is_garage === '1',
            garageSize: propertyData.garage_cars?.toString() || '',
            is_show_on_website:
              propertyData.is_show_on_website === 'active' ||
              propertyData.is_show_on_website === true ||
              propertyData.is_show_on_website === 1 ||
              propertyData.is_show_on_website === '1',
            is_featured: Boolean(propertyData.is_featured),
            is_area_unit:
              propertyData.is_area_unit === '1' ||
              propertyData.is_area_unit === 1 ||
              propertyData.is_area_unit === true,
            show_nearby: Boolean(propertyData.show_nearby),
            video_chat: Boolean(propertyData.video_chat),
            images: formattedImages,
            documents: formattedDocuments,
            propertyVideo: formattedPropertyVideo,
            // droneVideo: formattedDroneVideo,
            droneVideo: formattedDroneVideo,
            features: formattedFeatures,
            floorPlans: formattedFloorPlans,
            stagedImages: formattedStagedImages,
            view360Url: formattedView360Url,
            propertyVideoThumbnail: formattedPropertyVideoThumbnail,
            droneVideoThumbnail: formattedDroneVideoThumbnail
          };
          // console.log('proeprty ka data yeh ha ', propertyData?.drone_video?.file);
          setForm((prev) => ({
            ...prev,
            ...formattedData
          }));
        }
      } catch (error) {
        console.error('Failed to fetch property data:', error);
        toast.error('Failed to load property data for editing.');
      } finally {
        stopLoading('loadingProperty');
      }
    };

    fetchPropertyData();
  }, [isEditMode, editId]);

  const previousTabRef = useRef(form.activeTab);

  useEffect(() => {
    if (previousTabRef.current === 0 && form.activeTab !== 0) {
      // Find state and city names
      const stateObj = states.find((s) => s.id === form.state);
      const cityObj = cities.find((c) => c.id === form.city);

      const basicData = {
        title: form.title,
        state: form.state,
        city: form.city,
        description: form.description,
        state_name: stateObj?.name || '',
        city_name: cityObj?.name || ''
      };

      // console.log('✅ User left Basic Details tab. Current data:', basicData);
      setBasicTabDataForSEO(basicData);
    }
    previousTabRef.current = form.activeTab;
  }, [form.activeTab, form.title, form.state, form.city, form.description, states, cities]);

  // Helper functions
  const getTypeId = (typeName) => {
    const type = typeOptions.find((t) => t.name === typeName);
    return type ? type.id : null;
  };

  const getSelectedFeatureIds = () => {
    const selectedIds = [];
    featuresOptions.forEach((category) => {
      if (category.features && Array.isArray(category.features)) {
        category.features.forEach((feature) => {
          if (form.features[feature.name]) {
            selectedIds.push(feature.id);
          }
        });
      }
      if (category.sub_categories && Array.isArray(category.sub_categories)) {
        category.sub_categories.forEach((subCategory) => {
          if (subCategory.features && Array.isArray(subCategory.features)) {
            subCategory.features.forEach((feature) => {
              if (form.features[feature.name]) {
                selectedIds.push(feature.id);
              }
            });
          }
        });
      }
    });
    return selectedIds.join(',');
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  //  Tab change with loading check
  const handleTabChange = (e, newValue) => {
    if (isAnythingLoading()) {
      toast.info('Please wait until current operation completes');
      return;
    }
    setField('activeTab', newValue);
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setField(name, checked);
    } else {
      setField(name, value);
    }
  };

  // Modal handlers
  const handleLocationClick = () => setIsMapModalOpen(true);
  const closeMapModal = () => setIsMapModalOpen(false);
  const handleBackToManage = () => navigate('/properties');

  const MIN_IMAGES = 5;
  const MAX_IMAGES = 50;

  // Image handlers with context
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Calculate current total images (existing + uploading)
    const currentTotal = form.images.length + uploadingImages.length;

    // Check if minimum requirement is being enforced
    const availableSlots = MAX_IMAGES - currentTotal;

    // If already at max, prevent upload
    if (currentTotal >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed. You already have ${currentTotal} images.`);
      e.target.value = null;
      return;
    }

    // If adding these files would exceed limit, only take what fits
    let filesToUpload = files;
    if (files.length > availableSlots) {
      toast.warning(
        `Only ${availableSlots} more images can be added (limit: ${MAX_IMAGES}). Uploading first ${availableSlots} images.`
      );
      filesToUpload = files.slice(0, availableSlots);
    }

    const newUploadingImages = filesToUpload.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      isUploading: true,
      id: null
    }));

    setUploadingImages((prev) => [...prev, ...newUploadingImages]);
    startLoading('uploadingImages');

    try {
      const uploadPromises = filesToUpload.map(async (file, index) => {
        try {
          const response = await uploadPropertyMedia(file, 'image');

          setUploadingImages((prev) => prev.filter((img) => img.file !== file));

          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isDefault: form.images.length === 0 && index === 0,
            id: response.data?.id || response.id,
            serverUrl: response.data?.file || response.url,
            isUploading: false
          };
        } catch (error) {
          console.error('Upload failed:', error);
          setUploadingImages((prev) => prev.filter((img) => img.file !== file));

          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isDefault: false,
            uploadError: true,
            isUploading: false
          };
        }
      });

      const uploadedImages = await Promise.all(uploadPromises);
      setForm((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
    } finally {
      stopLoading('uploadingImages');
      e.target.value = null;
    }
  };

  // Thumnail iamge upload
  const handleThumnailUpload = async (e) => {
    // console.log('Handle thumnail upload');
  };

  const setDefaultImage = (index) => {
    setForm((prev) => {
      const images = prev.images.map((img, i) => ({ ...img, isDefault: i === index }));
      return { ...prev, images };
    });
  };

  const removeImage = async (index) => {
    const imageToRemove = form.images[index];
    const currentTotal = form.images.length;

    if (currentTotal <= MIN_IMAGES) {
      toast.error(`Minimum ${MIN_IMAGES} images required. Cannot remove more images.`);
      return;
    }

    if (imageToRemove.id) {
      try {
      } catch (error) {
        console.error('Failed to delete from server:', error);
      }
    }

    setForm((prev) => {
      const images = prev.images.filter((_, i) => i !== index);
      return { ...prev, images };
    });

    toast.success('Image removed successfully');
  };

  // Document handlers with context
  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newUploadingDocs = files.map((file) => ({
      file,
      name: file.name,
      isUploading: true,
      id: null
    }));

    setUploadingDocuments((prev) => [...prev, ...newUploadingDocs]);
    startLoading('uploadingDocuments');

    try {
      const uploadPromises = files.map(async (file) => {
        try {
          const response = await uploadPropertyDocuments(file, 'document');

          setUploadingDocuments((prev) => prev.filter((doc) => doc.file !== file));

          return {
            file,
            name: file.name,
            id: response.data.id,
            serverUrl: response.data.file,
            isUploading: false
          };
        } catch (error) {
          console.error('Document upload failed:', error);
          setUploadingDocuments((prev) => prev.filter((doc) => doc.file !== file));

          return {
            file,
            name: file.name,
            uploadError: true,
            isUploading: false,
            errorMessage: error.response?.data?.message || 'Upload failed'
          };
        }
      });

      const uploadedDocuments = await Promise.all(uploadPromises);
      setForm((prev) => ({
        ...prev,
        documents: [...prev.documents, ...uploadedDocuments]
      }));
    } finally {
      stopLoading('uploadingDocuments');
      e.target.value = null;
    }
  };

  const removeDocument = async (index) => {
    const docToRemove = form.documents[index];

    if (docToRemove.id) {
      try {
      } catch (error) {
        console.error('Failed to delete from server:', error);
      }
    }

    setForm((prev) => {
      const documents = prev.documents.filter((_, i) => i !== index);
      return { ...prev, documents };
    });
  };

  const handleDocumentClick = (documentUrl) => {
    window.open(documentUrl, '_blank');
  };

  // Video handlers with context
  const handleVideoSelect = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const fileType = type === 'property' ? 'video' : 'drone_video';

      const response = await uploadPropertyMedia(file, fileType);

      const videoObj = {
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        id: response.data.id,
        serverUrl: response.data.file,
        fileType: fileType
      };

      if (type === 'property') {
        setForm((prev) => ({ ...prev, propertyVideo: videoObj }));
      } else {
        setForm((prev) => ({ ...prev, droneVideo: videoObj }));
      }
    } catch (error) {
      console.error(`${type} video upload failed:`, error);
    } finally {
      e.target.value = null;
    }
  };

  const removeVideo = (type) => {
    if (type === 'property') setField('propertyVideo', null);
    else setField('droneVideo', null);
  };

  // Feature handlers
  const toggleFeature = (name) => {
    setForm((prev) => ({ ...prev, features: { ...prev.features, [name]: !prev.features[name] } }));
  };

  // Floor plan handlers
  const addFloorPlan = () => {
    setForm((prev) => {
      const nextId = prev.floorPlans.length ? Math.max(...prev.floorPlans.map((p) => p.id)) + 1 : 1;
      const newFloorPlan = {
        id: nextId,
        title: '',
        bedrooms: '',
        bathrooms: '',
        floorPrice: '',
        floorSize: '',
        notes: '',
        image: null
      };

      return {
        ...prev,
        floorPlans: [newFloorPlan, ...prev.floorPlans],
        expandedFloorPlan: nextId
      };
    });
  };

  // Floor plan image upload with context
  const handleFloorPlanImageUpload = async (file, floorPlanId) => {
    startLoading('uploadingFloorPlans');

    try {
      updateFloorPlan(floorPlanId, 'image', {
        preview: URL.createObjectURL(file),
        name: file.name,
        isUploading: true,
        serverUrl: null,
        uploadError: false
      });

      const response = await UploadFloorPlans(file, 'image');

      updateFloorPlan(floorPlanId, 'image', {
        id: response.data?.id || response.id,
        serverUrl: response.data?.file || response.data?.url || response.file || response.url,
        preview: response.data?.file || response.data?.url || response.file || response.url,
        name: file.name,
        isUploading: false,
        uploadError: false
      });

      return response;
    } catch (error) {
      console.error('Floor plan image upload failed:', error);
      updateFloorPlan(floorPlanId, 'image', {
        preview: URL.createObjectURL(file),
        name: file.name,
        isUploading: false,
        uploadError: true,
        errorMessage: error.response?.data?.message || 'Upload failed'
      });
      throw error;
    } finally {
      stopLoading('uploadingFloorPlans');
    }
  };

  const updateFloorPlan = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      floorPlans: prev.floorPlans.map((fp) => (fp.id === id ? { ...fp, [key]: value } : fp))
    }));
  };

  const removeFloorPlan = (id) => {
    setForm((prev) => ({ ...prev, floorPlans: prev.floorPlans.filter((fp) => fp.id !== id) }));
  };

  //Staged image handlers with context
  const handleStagedImageUpload = async (e, index, obj) => {
    if (obj?.paired) {
      startLoading('uploadingStagedImages');

      try {
        const file = obj.paired.file;

        const updatedStagedImages = [...form.stagedImages];
        updatedStagedImages[index] = {
          ...updatedStagedImages[index],
          paired: {
            file,
            preview: obj.paired.preview,
            name: file.name,
            isUploading: true,
            uploadError: false
          }
        };
        setForm((prev) => ({ ...prev, stagedImages: updatedStagedImages }));

        const response = await uploadStaggedImages(file, false);

        const finalUpdatedStagedImages = [...form.stagedImages];
        finalUpdatedStagedImages[index] = {
          ...finalUpdatedStagedImages[index],
          paired: {
            ...finalUpdatedStagedImages[index].paired,
            id: response.data?.id || response.id,
            serverUrl: response.data?.file || response.data?.url || response.file || response.url,
            isUploading: false,
            uploadError: false
          }
        };

        setForm((prev) => ({ ...prev, stagedImages: finalUpdatedStagedImages }));
        toast.success('Paired image uploaded successfully!');
      } catch (error) {
        console.error('Pair image upload failed:', error);
        const updatedStagedImages = [...form.stagedImages];
        updatedStagedImages[index] = {
          ...updatedStagedImages[index],
          paired: {
            ...obj.paired,
            uploadError: true,
            isUploading: false,
            errorMessage: error.response?.data?.message || 'Upload failed'
          }
        };
        setForm((prev) => ({ ...prev, stagedImages: updatedStagedImages }));
        toast.error('Failed to upload paired image');
      } finally {
        stopLoading('uploadingStagedImages');
      }
      return;
    }

    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    startLoading('uploadingStagedImages');

    try {
      const newUploadingImages = files.map((file, fileIndex) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        isDefault: form.stagedImages.length === 0 && fileIndex === 0,
        isUploading: true,
        uploadError: false,
        paired: null
      }));

      setForm((prev) => ({
        ...prev,
        stagedImages: [...prev.stagedImages, ...newUploadingImages]
      }));

      const uploadPromises = files.map(async (file, fileIndex) => {
        try {
          const response = await uploadStaggedImages(file, fileIndex === 0);

          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isDefault: form.stagedImages.length === 0 && fileIndex === 0,
            id: response.data?.id || response.id,
            serverUrl: response.data?.file || response.data?.url || response.file || response.url,
            isUploading: false,
            uploadError: false,
            paired: null
          };
        } catch (error) {
          console.error('Staged image upload failed:', error);
          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            isDefault: false,
            uploadError: true,
            isUploading: false,
            errorMessage: error.response?.data?.message || 'Upload failed',
            paired: null
          };
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      setForm((prev) => {
        const existingImages = prev.stagedImages.filter(
          (img) => !newUploadingImages.some((newImg) => newImg.preview === img.preview)
        );
        return {
          ...prev,
          stagedImages: [...existingImages, ...uploadResults]
        };
      });

      toast.success('Staged images uploaded successfully!');
    } catch (error) {
      console.error('Bulk staged images upload failed:', error);
      toast.error('Failed to upload some images');
    } finally {
      stopLoading('uploadingStagedImages');
    }
  };

  const removeStagedImage = (index) => {
    setForm((prev) => {
      const stagedImages = prev.stagedImages.filter((_, i) => i !== index);
      return { ...prev, stagedImages };
    });
  };

  const setDefaultStagedImage = (index) => {
    setForm((prev) => {
      const stagedImages = prev.stagedImages.map((img, i) => ({ ...img, isDefault: i === index }));
      return { ...prev, stagedImages };
    });
  };
  const getAreaUnitName = (unitId) => {
    if (!unitId) return '';
    const unit = areaUnitsOptions.find((u) => u.id === unitId);
    return unit ? unit.name : '';
  };
  const getAreaUnitId = (unitName) => {
    if (!unitName) return null;
    const unit = areaUnitsOptions.find((u) => u.name === unitName);
    return unit ? unit.id : null;
  };
  // form submition for property payload
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      startLoading('submitting');

      const defaultImage = form.images.find((img) => img.isDefault);
      const mainImageId = defaultImage ? defaultImage.id : form.images[0]?.id || null;
      const floorPlansData = form.floorPlans.map((fp) => ({
        id: isEditMode ? fp.id : undefined,
        title: fp.title || `Floor Plan ${fp.id}`,
        bedrooms: parseInt(fp.bedrooms) || 0,
        bathrooms: parseInt(fp.bathrooms) || 0,
        size: parseFloat(fp.floorSize) || 0,
        price: parseFloat(fp.floorPrice) || 0,
        description: fp.notes || '',
        image_id: fp.image?.id || null
      }));
      const getStatusId = (statusName) => {
        const status = statusOptions.find((s) => s.name === statusName);
        return status ? status.id : null;
      };

      const getAreaUnitId = (unitName) => {
        const unit = areaUnitsOptions.find((u) => u.name === unitName);
        return unit ? unit.id : null;
      };
      const propertyData = {
        id: isEditMode ? editId : undefined,
        title: form.title,
        address: form.address,
        state: form.state,
        city: form.city,
        postal_code: form.postalCode,
        area: form.area,
        // country: form.country,
        country: 'United States',
        lat: form.lat !== null ? parseFloat(form.lat) : null,
        lon: form.lon !== null ? parseFloat(form.lon) : null,
        garage_cars: parseInt(form.garageSize || form.garage) || 0,
        description: form.description,
        property_status: getStatusId(form.status),
        price: parseFloat(form.price) || 0,
        property_type_id: getTypeId(form.type),
        year_built: form.buildYear ? parseInt(form.buildYear.split('-')[0]) : null,
        // area_unit: form.areaUnit,
        area_unit_id: getAreaUnitId(form.areaUnit),
        area_size: parseFloat(form.buildUpArea) || 0,
        lot_size: parseFloat(form.lotSize) || 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        garage: parseInt(form.garage) || 0,
        is_garage: form.is_garage ? 1 : 0,
        is_show_build_year: form.showBuildYear ? 1 : 0,
        is_show_area: form.showArea ? 1 : 0,
        status: form.is_show_on_website ? 'active' : 'inactive',
        is_show_on_website: form.is_show_on_website ? 1 : 0,
        is_featured: form.is_featured ? 1 : 0,
        is_area_unit: form.is_area_unit ? 1 : 0,
        show_nearby: form.show_nearby ? 1 : 0,
        video_chat: form.video_chat ? 1 : 0,
        view_3d: form.view360Url,
        slug: form.slug,
        meta_title: form.metaTitle,
        meta_description: form.metaDescription,
        is_main_image_id: mainImageId,
        image_ids: form.images
          .filter((img) => !img.uploadError && img.id)
          .map((img) => img.id)
          .join(','),
        attachment_ids: form.documents
          .filter((doc) => !doc.uploadError && doc.id)
          .map((doc) => doc.id)
          .join(','),

        video_id: form.propertyVideo?.id || null,
        drone_video_id: form.droneVideo?.id || null,
        // thumnail_ids: [form.propertyVideoThumbnail?.id, form.droneVideoThumbnail?.id]
        //   .filter(Boolean)
        //   .join(','),

        video_thumbnail_id: form.propertyVideoThumbnail?.id || null,
        drone_thumbnail_id: form.droneVideoThumbnail?.id || null,

        // thumnail : form.propertyVideoThumbnail?.id || null,
        featured_ids: getSelectedFeatureIds(),
        property_floor_details: JSON.stringify(floorPlansData),
        stage_images: JSON.stringify(
          form.stagedImages
            .filter((img) => !img.uploadError && img.id && img.paired?.id)
            .map((img) => ({
              from_id: img.id,
              to_id: img.paired.id
            }))
        ),
        view_360_image_ids: form.view360Images
          .filter((img) => !img.uploadError && img.id)
          .map((img) => img.id)
          .join(',')
      };

      const response = isEditMode
        ? await updateProperty(propertyData)
        : await addProperty(propertyData);

      toast.success(`Property ${isEditMode ? 'updated' : 'added'} successfully!`, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      setTimeout(() => {
        navigate('/properties');
      }, 1500);
    } catch (error) {
      // console.error(
      //   `Failed to ${isEditMode ? 'update' : 'save'} property:`,
      //   error.response?.data || error.message
      // );
      // console.log(error.response?.data);
      // toast.error(`Failed to ${isEditMode ? 'update' : 'save'} property. Please try again.`, {
      //   position: 'top-right',
      //   autoClose: 3000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true
      // });
      toast.error(error.response?.data?.error, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      stopLoading('submitting');
    }
  };
  // Tab configuration
  const tabs = [
    {
      label: 'Basic Details',
      component: (
        <BasicDetailTab
          form={form}
          handleInput={handleInput}
          inputRef={inputRef}
          handleLocationClick={handleLocationClick}
          loadingProperty={loadingStates.loadingProperty}
          loadingStatus={loadingStates.loadingStatus}
          statusOptions={statusOptions}
          typeOptions={typeOptions}
          areaUnitOptions={areaUnitsOptions}
          show_nearby={form.show_nearby}
          video_chat={form.video_chat}
          setField={setField}
          states={states}
          cities={cities}
          loadingStates={false}
          Manage
          Properties
          loadingCities={false}
          onStateChange={handleStateChange}
          areaUnitDisplayValue={getAreaUnitName(form.areaUnit) || form.areaUnit}
          triggerAddressSearch={triggerAddressSearch}
          onDataSubmit={handleBasicTabData}
        />
      )
    },
    {
      label: 'SEO',
      component: (
        <SeoTab
          form={form}
          handleInput={handleInput}
          setField={setField}
          states={states}
          cities={cities}
          existingSlug={form.slug}
          existingMetaTitle={form.meta_title}
          existingMetaDescription={form.meta_description}
          basicTabData={basicTabDataForSEO}
        />
      )
    },
    {
      label: 'Pictures',
      component: (
        <PropertyImagesTab
          form={form}
          uploadingImages={uploadingImages}
          handleImageUpload={handleImageUpload}
          setDefaultImage={setDefaultImage}
          removeImage={removeImage}
          loadingProperty={loadingStates.loadingProperty}
        />
      )
    },
    {
      label: 'Documents',
      component: (
        <PropertyDocumentsTab
          form={form}
          uploadingDocuments={uploadingDocuments}
          handleDocUpload={handleDocUpload}
          removeDocument={removeDocument}
          handleDocumentClick={handleDocumentClick}
          loadingProperty={loadingStates.loadingProperty}
        />
      )
    },
    {
      label: 'Videos',
      component: (
        <VideosTab
          form={form}
          uploadingVideos={loadingStates.uploadingVideos}
          handleVideoSelect={handleVideoSelect}
          removeVideo={removeVideo}
          loadingProperty={loadingStates.loadingProperty}
          handleThumnailUpload={handleThumnailUpload}
          handleVideoThumbnailUpload={handleVideoThumbnailUpload}
          removeVideoThumbnail={removeVideoThumbnail}
          uploadingVideoThumbnails={uploadingVideoThumbnails}
          isAnythingLoading={isAnythingLoading}
        />
      )
    },
    {
      label: 'Specifications ',
      component: (
        <FactsFeaturesTab
          form={form}
          featuresOptions={featuresOptions}
          toggleFeature={toggleFeature}
          loadingProperty={loadingStates.loadingProperty}
        />
      )
    },
    {
      label: 'Floor Plans',
      component: (
        <FloorPlansTab
          form={form}
          addFloorPlan={addFloorPlan}
          updateFloorPlan={updateFloorPlan}
          removeFloorPlan={removeFloorPlan}
          handleFloorPlanImageUpload={handleFloorPlanImageUpload}
          loadingProperty={loadingStates.loadingProperty}
          setField={setField}
          areaUnit={form.areaUnit}
        />
      )
    },
    {
      label: 'Staged Images',
      component: (
        <StagedImagesTab
          stagedImages={form.stagedImages}
          onUploadStagedImages={handleStagedImageUpload}
          onRemoveStagedImage={removeStagedImage}
          loadingProperty={loadingStates.loadingProperty}
        />
      )
    },
    {
      label: '360 View',
      component: (
        <View360Tab
          view360Url={form.view360Url}
          onUrlChange={(url) => setForm((prev) => ({ ...prev, view360Url: url }))}
          loadingProperty={loadingStates.loadingProperty}
        />
      )
    }
  ];

  // Check if anything is loading
  const isDisabled = isAnythingLoading();

  return (
    <Page
      title={`${isEditMode ? 'Edit' : 'Add'} Properties | ${process.env.REACT_APP_PROJECTNAME}`}
    >
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <ToastContainer />
        <ViewOnMap
          show={isMapModalOpen}
          onClose={closeMapModal}
          currentAddress={form.address}
          currentLat={form.lat}
          currentLon={form.lon}
          onAddressSelect={(address, lat, lng) =>
            setForm((prev) => ({
              ...prev,
              address: address,
              lat: lat,
              lon: lng
            }))
          }
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mt: 1,
            pb: 2,
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 25 },
              fontFamily: 'poppinsSemiBold',
              mb: { xs: 1, sm: 2 },
              color: palette.primary.titledark,
              width: { xs: '100%', sm: 'auto' },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            {isEditMode ? 'Edit Property' : 'Add Property'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Button
              variant="contained"
              onClick={handleBackToManage}
              disabled={isDisabled}
              sx={{
                background: palette.primary.main,
                color: palette.primary.contrastText,
                height: { xs: 48, sm: 40 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': { background: palette.primary.dark },
                '&:disabled': { background: palette.grey[400] },
                fontSize: { xs: '14px', sm: '13px' }
              }}
            >
              Back to Manage Property
            </Button>
            {/* Save/Update button with loading state */}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabled}
              sx={{
                background: palette.primary.main,
                color: palette.primary.contrastText,
                height: { xs: 48, sm: 40 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': { background: palette.primary.dark },
                '&:disabled': { background: palette.grey[400] },
                fontSize: { xs: '14px', sm: '13px' }
              }}
            >
              {loadingStates.submitting
                ? 'Saving...'
                : `${isEditMode ? 'Update' : 'Save'} Property`}
            </Button>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ p: { xs: 1, md: 2 } }}>
          {/* Tabs with loading state */}
          <Tabs
            value={form.activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              '& .MuiTabs-indicator': { backgroundColor: palette.primary.main },
              '& .MuiTab-root': { textTransform: 'none' },
              '& .Mui-disabled': { opacity: 0.5 }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                disabled={isDisabled && form.activeTab !== index}
              />
            ))}
          </Tabs>

          <Divider />

          <CardContent sx={{ pt: 2 }}>
            {loadingStates.loadingProperty ? (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={200} />
              </Box>
            ) : (
              <>
                {tabs.map((tab, index) => (
                  <Box key={index} sx={{ display: form.activeTab === index ? 'block' : 'none' }}>
                    {tab.component}
                  </Box>
                ))}
              </>
            )}
          </CardContent>

          <Divider />

          <CardContent
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Navigation buttons with loading state */}
              <Button
                variant="outlined"
                onClick={() => setField('activeTab', Math.max(0, form.activeTab - 1))}
                disabled={form.activeTab === 0 || isDisabled}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                onClick={() => setField('activeTab', Math.min(tabs.length - 1, form.activeTab + 1))}
                disabled={form.activeTab === tabs.length - 1 || isDisabled}
              >
                Next
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
}
